import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TrendingUp, Phone, Target, DollarSign, ShieldOff, AlertTriangle } from "lucide-react";

const Stat = ({ label, value, accent, testid }) => (
  <div className="bg-white border border-border rounded-sm p-4" data-testid={testid}>
    <div className={`font-serif text-3xl ${accent || "text-navy"}`}>{value}</div>
    <div className="text-xs uppercase text-mutedsea tracking-wide mt-1">{label}</div>
  </div>
);

export default function BOIDashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/boi/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);
  if (!d) return <div className="p-8 text-mutedsea text-sm" data-testid="boi-dashboard-loading">Loading dashboard…</div>;
  const money = (n) => `$${Number(n || 0).toLocaleString()}`;

  return (
    <div data-testid="boi-dashboard-page">
      <PageHeader title={d.is_admin ? "BOI Management Dashboard" : "My BOI Dashboard"} subtitle="Acquisition activity, pipeline and estimated relationship value." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat testid="stat-rel-added" label="Relationships" value={d.relationships_added} />
        <Stat testid="stat-active" label="Active" value={d.relationships_active} />
        <Stat testid="stat-calls-today" label="Calls Today" value={d.calls_today} accent="text-atlantic" />
        <Stat testid="stat-calls-week" label="Calls This Week" value={d.calls_this_week} accent="text-atlantic" />
        <Stat testid="stat-connections" label="Connections" value={d.connections} />
        <Stat testid="stat-meaningful" label="Meaningful Convos" value={d.meaningful_conversations} accent="text-seagreen" />
        <Stat testid="stat-appts" label="Appointments" value={d.appointments_set} accent="text-seagreen" />
        <Stat testid="stat-qualified" label="Qualified Opps" value={d.qualified_opportunities} accent="text-seagreen" />
        <Stat testid="stat-opps-created" label="Opps Created" value={d.opportunities_created} />
        <Stat testid="stat-opps-won" label="Opps Won" value={d.opportunities_won} accent="text-seagreen" />
        <Stat testid="stat-opps-lost" label="Opps Lost" value={d.opportunities_lost} accent="text-seared" />
        <Stat testid="stat-conversion" label="Conversion Rate" value={`${d.conversion_rate}%`} accent="text-atlantic" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat testid="stat-due" label="Follow-Ups Due" value={d.followups_due} accent="text-seaamber" />
        <Stat testid="stat-overdue" label="Overdue" value={d.overdue_followups} accent="text-seared" />
        <Stat testid="stat-pipeline" label="Pipeline Value" value={money(d.pipeline_value)} accent="text-navy" />
        <Stat testid="stat-est-value" label="Est. Relationship Value" value={money(d.estimated_relationship_value)} accent="text-atlantic" />
        <Stat testid="stat-actual" label="Actual Revenue" value={money(d.actual_revenue)} accent="text-seagreen" />
        <Stat testid="stat-dnc" label="DNC / Suppressed" value={d.dnc_count} accent="text-seared" />
        <Stat testid="stat-dq" label="Data Quality Issues" value={d.data_quality_issues} accent="text-seaamber" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-border rounded-sm p-4">
          <h2 className="font-serif text-lg text-navy mb-2 flex items-center gap-1.5"><Target className="w-4 h-4" />Top Prospects</h2>
          {d.top_prospects.map((r) => (
            <div key={r.id} onClick={() => nav(`/relationships/${r.id}`)} className="flex items-center justify-between py-1.5 border-b border-border last:border-0 cursor-pointer hover:bg-ice px-1 rounded-sm" data-testid={`top-prospect-${r.id}`}>
              <span className="text-sm text-navy">{r.name}</span>
              <span className="text-xs text-mutedsea">{r.best_next_opportunity?.business_line_name || "—"} · <span className="font-serif text-navy">{r.highest_score ?? 0}</span> · {money(r.est_value_total)}</span>
            </div>
          ))}
          {d.top_prospects.length === 0 && <p className="text-sm text-mutedsea">No relationships yet.</p>}
        </div>

        {d.is_admin && (
          <div className="bg-white border border-border rounded-sm p-4" data-testid="producer-activity">
            <h2 className="font-serif text-lg text-navy mb-2 flex items-center gap-1.5"><Phone className="w-4 h-4" />Producer Activity (this week)</h2>
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase text-mutedsea"><th className="text-left py-1">Producer</th><th className="text-right">Attempts</th><th className="text-right">Meaningful</th><th className="text-right">Appts</th></tr></thead>
              <tbody>
                {d.producer_activity.map((p) => (
                  <tr key={p.producer} className="border-t border-border"><td className="py-1 text-navy">{p.producer}</td><td className="text-right">{p.attempts}</td><td className="text-right">{p.meaningful}</td><td className="text-right">{p.appointments}</td></tr>
                ))}
              </tbody>
            </table>
            {d.producer_activity.length === 0 && <p className="text-sm text-mutedsea">No activity this week.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
